import { Link, createFileRoute, useLocation, redirect, notFound } from '@tanstack/react-router'
import { useEffect } from 'react'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BlogPost } from '@/data/blog-posts'
import { ssrBlogPost } from '@/server/ssr-data'
import { getD1 } from '@/server/d1-context'
import blogService from '@/services/blog.service'
import { ApiError } from '@/services/api'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { RelatedPosts } from '@/components/blog/RelatedPosts'
import { BlogPostPreview } from '@/components/blog/BlogPostPreview'
import { PageContainer } from '@/components/layout/PageContainer'
import { SectionContainer } from '@/components/layout/SectionContainer'
import { LoadingState } from '@/components/layout/LoadingState'
import { Button } from '@/components/ui/button'
import { useBlogPost } from '@/hooks/useBlogQueries'
import { usePublicTags } from '@/hooks/useTagQueries'
import { usePublicCategories } from '@/hooks/useCategoryQueries'
import {
  localizeBlogCategory,
  localizeBlogReadTime,
  localizeBlogTag,
} from '@/lib/blog-i18n'
import { designSystem } from '@/lib/design-system'
import {
  getLocaleDir,
  getLocaleFromPath,
  withLocalePath,
} from '@/lib/i18n-utils'
import { cn } from '@/lib/utils'
import { img } from '@/lib/image'
import { SEO_ORIGIN } from '@/lib/company'
import {
  blogPostingLd,
  breadcrumbLd,
  buildLocalizedSeoHead,
  jsonLd,
  navLabel,
} from '@/lib/seo-head'

export const Route = createFileRoute('/{-$locale}/blog/$slug')({
  loader: async ({ params }) => {
    const read = async (locale: string) => {
      if (getD1()) return ssrBlogPost(params.slug, locale)
      try { return await blogService.fetchBlogPostBySlug(params.slug, locale) }
      catch (error) { if (error instanceof ApiError && error.response?.status === 404) return null; throw error }
    }
    const locale = params.locale ?? 'en-US'
    const post = await read(locale)
    if (post) return post
    if (locale !== 'en-US') {
      const english = await read('en-US')
      if (english) throw redirect({ href: withLocalePath(`/blog/${english.slug}`, 'en-US'), statusCode: 302 })
    }
    throw notFound()
  },
  head: ({ loaderData, matches, params }) => {
    if (matches[0]?.globalNotFound) return {}
    const post = loaderData as any
    const title = `${post?.title ?? 'Blog'} — Horizon Tech`
    const description = post?.excerpt ?? 'Read our latest blog post.'
    const rawImage = post?.bannerImage ?? post?.coverImage
    const ogImage = rawImage
      ? `https://www.horizon-tech.io${img(rawImage, { width: 1200, format: 'auto' })}`
      : 'https://www.horizon-tech.io/clean-square.png'
    const locale = params.locale ?? 'en-US'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:type', content: 'article' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:image', content: ogImage },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage },
      ],
      scripts: post
        ? [
            jsonLd(
              blogPostingLd({
                url: `${SEO_ORIGIN}${withLocalePath(`/blog/${post.slug}`, locale)}`,
                title: post.title,
                description,
                image: ogImage,
                datePublished: post.publishedAt,
                author: post.authorName,
                locale,
              }),
            ),
            jsonLd(
              breadcrumbLd([
                {
                  name: navLabel(locale, 'mobileNav.home'),
                  url: buildLocalizedSeoHead('/', locale).canonicalUrl,
                },
                {
                  name: navLabel(locale, 'nav.writing'),
                  url: buildLocalizedSeoHead('/blog', locale).canonicalUrl,
                },
                {
                  name: post.title,
                  url: `${SEO_ORIGIN}${withLocalePath(`/blog/${post.slug}`, locale)}`,
                },
              ]),
            ),
          ]
        : [],
    }
  },
  component: BlogPostPage,
})

function upsertMetaTag(
  attribute: 'name' | 'property',
  key: string,
  content: string,
) {
  let node = document.head.querySelector(`meta[${attribute}="${key}"]`)
  if (!node) {
    node = document.createElement('meta')
    node.setAttribute(attribute, key)
    document.head.appendChild(node)
  }
  node.setAttribute('content', content)
}

function BlogPostPage() {
  const { t } = useTranslation(['pages', 'blog'])
  const { slug } = Route.useParams()
  const location = useLocation()
  const currentLocale = getLocaleFromPath(location.pathname)

  // Use SSR loader data as initial query cache — no double-fetch
  const loaderPost = Route.useLoaderData()
  const { data: apiPost, isPending } = useBlogPost(
    slug,
    currentLocale,
    loaderPost,
  )
  const post = apiPost
  const { data: tags = [] } = usePublicTags()
  const { data: categories = [] } = usePublicCategories()

  // R1/R11: the SSR loader never 404s on a D1 miss (loaderPost === null just
  // means "unknown"), so head() had nothing to render server-side. Once the
  // client query resolves the real post, patch the title/description/og tags
  // that head() left at their generic fallback. Only runs for that one case —
  // when the SSR loader already had the post, head() already rendered it.
  useEffect(() => {
    if (loaderPost !== null || !apiPost) return
    const title = `${apiPost.title} — Horizon Tech`
    document.title = title
    upsertMetaTag('name', 'description', apiPost.excerpt)
    upsertMetaTag('property', 'og:title', apiPost.title)
    upsertMetaTag('property', 'og:description', apiPost.excerpt)
  }, [loaderPost, apiPost])

  if (isPending && !post) {
    return (
      <PageContainer className="journal-blog-article">
        <SectionContainer spacing="lg" className="mx-auto max-w-5xl">
          <LoadingState message={t('error.loading.blog', 'Loading post...')} />
        </SectionContainer>
      </PageContainer>
    )
  }

  if (!post) {
    return (
      <PageContainer className="journal-blog-article">
        <SectionContainer
          spacing="lg"
          className="mx-auto max-w-5xl text-center"
        >
          <h1 className={cn(designSystem.typography.heading.h2, 'mb-3')}>
            {t('blogPost.notFound.title')}
          </h1>
          <p
            className={cn(
              designSystem.typography.body.base,
              designSystem.typography.muted,
              'mb-6',
            )}
          >
            {t('blogPost.notFound.description', { slug })}
          </p>
          <Button asChild>
            <Link to={withLocalePath('/blog', currentLocale)}>
              {t('blogPost.notFound.back')}
            </Link>
          </Button>
        </SectionContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="journal-blog-article">
      <SectionContainer spacing="lg" className="mx-auto max-w-5xl">
        <Button asChild variant="ghost" className="mb-6 px-0">
          <Link to={withLocalePath('/blog', currentLocale)}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            {t('blogPost.back')}
          </Link>
        </Button>

        <BlogPostPreview
          className="journal-blog-post"
          title={post.title}
          excerpt={post.excerpt}
          author={post.author}
          category={localizeBlogCategory(
            categories,
            post.category,
            currentLocale,
          )}
          publishedAt={post.publishedAt}
          readTime={localizeBlogReadTime(t, post.readTime)}
          content={post.content}
          tags={post.tags.map((tag) =>
            localizeBlogTag(tags, tag, currentLocale),
          )}
          authorSlug={post.authorSlug}
          authorAvatarUrl={post.authorAvatarUrl}
          locale={currentLocale}
          dir={getLocaleDir(currentLocale)}
          coverImage={post.coverImage}
          bannerImage={post.bannerImage}
        />

        {post.authorSlug && (
          <div className="my-10 flex items-center gap-4 rounded-lg border p-6">
            <Avatar className="h-14 w-14">
              {post.authorAvatarUrl && (
                <AvatarImage
                  src={img(post.authorAvatarUrl, { width: 96, format: 'auto' })}
                  alt={post.author}
                  width={56}
                  height={56}
                />
              )}
              <AvatarFallback>
                {post.author
                  .split(' ')
                  .map((p) => p[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p
                className={cn(
                  designSystem.typography.body.xs,
                  designSystem.typography.muted,
                  'mb-1 uppercase tracking-wider',
                )}
              >
                {t('blog:aboutAuthor', 'About the author')}
              </p>
              <p
                className={cn(
                  designSystem.typography.body.base,
                  'font-semibold',
                )}
              >
                {post.author}
              </p>
              <Link
                to={withLocalePath('/about', currentLocale)}
                className={cn(
                  designSystem.typography.body.small,
                  'text-primary hover:underline',
                )}
              >
                {t('blog:viewProfile', 'View profile')}
              </Link>
            </div>
          </div>
        )}

        <RelatedPosts currentPost={post} locale={currentLocale} />
      </SectionContainer>
    </PageContainer>
  )
}
